import type { FmOperationalDialogDescriptor } from './FmOperationalWorkspaceRoot.types';
import {
  exactOperationalKeys as exact,
  failOperationalDescriptor as fail,
  operationalArray as array,
  operationalObject as object,
  operationalOneOf as oneOf,
  operationalText as text,
  optionalOperationalText as optionalText,
} from './FmOperationalWorkspaceValidation.shared';

function validateDialog(dialogValue: unknown, index: number, revisionId: string): void {
  const path = `descriptor.dialogs[${index}]`;
  const dialog = object(dialogValue, path) as FmOperationalDialogDescriptor &
    Record<string, unknown>;
  text(dialog.id, `${path}.id`);
  if (typeof dialog.open !== 'boolean') fail(`${path}.open`, 'must be boolean');
  if (dialog.kind === 'confirmation') {
    exact(
      dialog,
      new Set([
        'kind',
        'id',
        'open',
        'title',
        'body',
        'confirmLabel',
        'cancelLabel',
        'actionId',
        'alternateLabel',
        'alternateActionId',
        'alternateDestructive',
        'expectedRevisionId',
        'expectedPayloadRevision',
        'expectedDeleteConsequenceRevision',
        'destructive',
        'dismissible',
      ]),
      path,
    );
    ['title', 'body', 'confirmLabel', 'cancelLabel', 'actionId'].forEach((key) =>
      text(dialog[key], `${path}.${key}`),
    );
    optionalText(dialog.expectedPayloadRevision, `${path}.expectedPayloadRevision`);
    optionalText(
      dialog.expectedDeleteConsequenceRevision,
      `${path}.expectedDeleteConsequenceRevision`,
    );
    optionalText(dialog.alternateLabel, `${path}.alternateLabel`);
    optionalText(dialog.alternateActionId, `${path}.alternateActionId`);
    if ((dialog.alternateLabel === undefined) !== (dialog.alternateActionId === undefined)) {
      fail(path, 'must provide alternateLabel and alternateActionId together');
    }
    if (
      dialog.alternateDestructive !== undefined &&
      typeof dialog.alternateDestructive !== 'boolean'
    ) {
      fail(`${path}.alternateDestructive`, 'must be boolean');
    }
    if (dialog.dismissible !== undefined && typeof dialog.dismissible !== 'boolean') {
      fail(`${path}.dismissible`, 'must be boolean');
    }
    if (text(dialog.expectedRevisionId, `${path}.expectedRevisionId`) !== revisionId)
      fail(`${path}.expectedRevisionId`, 'must match the canonical revision');
    return;
  }
  fail(`${path}.kind`, 'is not registered');
}

export function validateRevision(value: unknown): string {
  const revision = object(value, 'descriptor.revision');
  exact(
    revision,
    new Set(['id', 'version', 'label', 'status', 'observedAt']),
    'descriptor.revision',
  );
  const revisionId = text(revision.id, 'descriptor.revision.id');
  text(revision.label, 'descriptor.revision.label');
  oneOf(revision.status, ['current', 'stale', 'conflict'], 'descriptor.revision.status');
  if (!Number.isInteger(revision.version) || Number(revision.version) < 1)
    fail('descriptor.revision.version', 'must be a positive integer');
  optionalText(revision.observedAt, 'descriptor.revision.observedAt');
  return revisionId;
}

export function validateSelection(value: unknown, revisionId: string): void {
  const selection = object(value, 'descriptor.selection');
  if (selection.kind === 'none')
    exact(selection, new Set(['kind', 'revisionId']), 'descriptor.selection');
  else if (selection.kind === 'entity') {
    exact(
      selection,
      new Set(['kind', 'entityKind', 'entityId', 'label', 'revisionId']),
      'descriptor.selection',
    );
    ['entityKind', 'entityId', 'label'].forEach((key) =>
      text(selection[key], `descriptor.selection.${key}`),
    );
    oneOf(
      selection.entityKind,
      ['property', 'stand', 'plan', 'campaign', 'plot', 'tender'],
      'descriptor.selection.entityKind',
    );
  } else fail('descriptor.selection.kind', 'is not registered');
  if (text(selection.revisionId, 'descriptor.selection.revisionId') !== revisionId)
    fail('descriptor.selection.revisionId', 'must match the canonical revision');
}

export function validateMutation(value: unknown): void {
  const mutation = object(value, 'descriptor.mutation');
  exact(mutation, new Set(['status', 'label', 'progress', 'receipts']), 'descriptor.mutation');
  oneOf(
    mutation.status,
    ['idle', 'pending', 'succeeded', 'failed', 'conflict'],
    'descriptor.mutation.status',
  );
  text(mutation.label, 'descriptor.mutation.label');
  if (
    mutation.status === 'pending' &&
    mutation.progress !== undefined &&
    (typeof mutation.progress !== 'number' || mutation.progress < 0 || mutation.progress > 100)
  )
    fail('descriptor.mutation.progress', 'must be between 0 and 100 while pending');
  if (mutation.status !== 'pending' && mutation.progress !== undefined)
    fail('descriptor.mutation.progress', 'is only valid while pending');
  const receiptIds = new Set<string>();
  array(mutation.receipts, 'descriptor.mutation.receipts').forEach((item, index) => {
    const path = `descriptor.mutation.receipts[${index}]`;
    const receipt = object(item, path);
    const kind = oneOf(receipt.kind, ['blocker', 'success', 'information'], `${path}.kind`);
    // A blocker an owner cannot act on is not a receipt: the resolving control
    // is structural, not conventional.
    exact(
      receipt,
      new Set(
        kind === 'blocker'
          ? ['id', 'kind', 'code', 'message', 'resolvingActionId']
          : ['id', 'kind', 'code', 'message'],
      ),
      path,
    );
    receiptIds.add(text(receipt.id, `${path}.id`));
    ['code', 'message'].forEach((key) => text(receipt[key], `${path}.${key}`));
    if (kind === 'blocker') text(receipt.resolvingActionId, `${path}.resolvingActionId`);
  });
  if (receiptIds.size !== array(mutation.receipts, 'descriptor.mutation.receipts').length) {
    fail('descriptor.mutation.receipts', 'must use unique ids');
  }
}

export function validateBanners(value: unknown): void {
  const banners = array(value, 'descriptor.banners');
  const bannerIds = new Set<string>();
  banners.forEach((item, index) => {
    const path = `descriptor.banners[${index}]`;
    const banner = object(item, path);
    exact(banner, new Set(['id', 'tone', 'title', 'description']), path);
    bannerIds.add(text(banner.id, `${path}.id`));
    text(banner.title, `${path}.title`);
    oneOf(banner.tone, ['error', 'warning', 'info', 'success'], `${path}.tone`);
    optionalText(banner.description, `${path}.description`);
  });
  if (bannerIds.size !== banners.length) fail('descriptor.banners', 'must use unique ids');
}

export function validateControls(value: unknown, revisionId: string): Record<string, unknown>[] {
  const controls = array(value, 'descriptor.controls');
  const records = controls.map((item, index) => {
    const path = `descriptor.controls[${index}]`;
    const action = object(item, path);
    exact(
      action,
      new Set([
        'id',
        'label',
        'variant',
        'size',
        'disabled',
        'disabledReason',
        'availableWhenUnavailable',
        'tone',
        'expectedRevisionId',
        'confirmationId',
      ]),
      path,
    );
    text(action.id, `${path}.id`);
    text(action.label, `${path}.label`);
    if (action.variant !== undefined)
      oneOf(action.variant, ['default', 'bordered', 'primary', 'text', 'link'], `${path}.variant`);
    if (action.size !== undefined) oneOf(action.size, ['sm', 'md'], `${path}.size`);
    if (action.disabled !== undefined && typeof action.disabled !== 'boolean')
      fail(`${path}.disabled`, 'must be boolean');
    if (
      action.availableWhenUnavailable !== undefined &&
      typeof action.availableWhenUnavailable !== 'boolean'
    ) {
      fail(`${path}.availableWhenUnavailable`, 'must be boolean');
    }
    if (text(action.expectedRevisionId, `${path}.expectedRevisionId`) !== revisionId)
      fail(`${path}.expectedRevisionId`, 'must match the canonical revision');
    optionalText(action.disabledReason, `${path}.disabledReason`);
    optionalText(action.confirmationId, `${path}.confirmationId`);
    return action;
  });
  const ids = records.map((item, index) => text(item.id, `descriptor.controls[${index}].id`));
  if (new Set(ids).size !== ids.length) fail('descriptor.controls', 'must use unique ids');
  return records;
}

export function validateOperationalDialogs(
  value: unknown,
  revisionId: string,
  controls: Record<string, unknown>[],
): void {
  const dialogs = array(value, 'descriptor.dialogs');
  dialogs.forEach((item, index) => validateDialog(item, index, revisionId));
  const records = dialogs.map((item, index) => object(item, `descriptor.dialogs[${index}]`));
  const ids = records.map((item, index) => text(item.id, `descriptor.dialogs[${index}].id`));
  if (new Set(ids).size !== ids.length) fail('descriptor.dialogs', 'must use unique ids');
  const confirmationIds = new Set(records.map((item) => String(item.id)));
  controls.forEach((action, index) => {
    if (
      action.confirmationId !== undefined &&
      !confirmationIds.has(String(action.confirmationId))
    ) {
      fail(`descriptor.controls[${index}].confirmationId`, 'must reference a confirmation dialog');
    }
  });
  records.forEach((dialog, index) => {
    const action = controls.find((item) => item.id === String(dialog.actionId));
    if (!action || action.confirmationId !== dialog.id)
      fail(`descriptor.dialogs[${index}].actionId`, 'must reference its confirmation action');
  });
}
